import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type ReactAnalytics from 'garaje/services/react-analytics';
import type ReactRendererService from 'garaje/services/react-renderer';
import type { ComponentType } from 'react';
import type { Root } from 'react-dom/client';

/**
 * Glimmer wrapper for the React GlobalSettingsLinkCard federated from RRC.
 *
 * Rendered as the last card on `/communications/settings`, this pointer card
 * links users to the organization-wide notification-rules surface (VIPs,
 * traveling employees, near-home addresses). The link click delegates to
 * RRC's `reactRichComponentsConfig.navigate`, which the react-renderer
 * service wires to the Ember router — so the destination transition stays
 * in-SPA rather than reloading the page.
 */
export default class GlobalSettingsLinkCardComponent extends Component {
  @service declare reactRenderer: ReactRendererService;
  @service declare reactAnalytics: ReactAnalytics;

  private root?: Root;

  @action
  async injectComponent(): Promise<void> {
    const element = document.getElementById('react__global-settings-link-card');
    if (!element) {
      return;
    }

    // Guarded against RRC deploy skew and chunk-load failures. This card is
    // ungated on `/communications/settings`, so a failed import would
    // otherwise surface as an unhandled promise rejection for every visitor.
    // Route the two failure modes (import throws / export missing) through
    // `reactAnalytics.trackMonitoring` (same pattern as react-renderer's
    // `RRC_STYLE_FETCH_FAILED`) so engineering has diagnostics without
    // showing an error toast for what's a decorative pointer card.
    try {
      // The ambient shim (types/reactRichComponents/communication/GlobalSettingsLinkCard.d.ts)
      // declares the export as present, but at runtime a deploy-skewed RRC bundle may not have
      // it — so narrow the awaited import to an optional shape and guard below. Casting to
      // ComponentType also lets the reactRenderer.render call typecheck without a
      // no-unsafe-argument disable further down.
      const { GlobalSettingsLinkCard } = (await import('reactRichComponents/communication/GlobalSettingsLinkCard')) as {
        GlobalSettingsLinkCard?: ComponentType;
      };

      if (!GlobalSettingsLinkCard || typeof GlobalSettingsLinkCard !== 'function') {
        this.reactAnalytics.trackMonitoring('GLOBAL_SETTINGS_LINK_CARD_EXPORT_MISSING');
        return;
      }

      this.root = await this.reactRenderer.render(GlobalSettingsLinkCard, 'react__global-settings-link-card');
    } catch (error) {
      this.reactAnalytics.trackMonitoring('GLOBAL_SETTINGS_LINK_CARD_LOAD_FAILED', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  @action
  destroyComponent(): void {
    if (this.root) {
      this.reactRenderer.unmount(this.root);
      this.root = undefined;
    }
  }
}
